/**
 * @file The player's "pulse" — the kill-timer ratio, read for the pulse-driven
 * post-processing (`PostProcessing.js`). Kept Three.js-free so the value can
 * be unit-tested without a WebGL context.
 *
 * This is the same ratio the debug hub already labels "Pulse"
 * (`DebugPanel.js`) and that `MusicCueSystem.js` gates its music pulse on —
 * one source of truth, read here instead of recomputed.
 */

import { Health } from '../components/index.js';

/**
 * @param {import('../ecs/Entity.js').Entity | null} player Current player entity.
 * @returns {number} 0..1. 1 when the kill-timer is full or the player has no
 *   kill-timer at all (nothing to react to, treated as "calm").
 */
export function samplePulseRatio(player) {
  const health = player?.alive ? player.get(Health) : null;
  return health?.usesKillTimer ? health.ratio : 1;
}

/**
 * @param {import('../ecs/Entity.js').Entity | null} player Current player entity.
 * @returns {number} 0..1. 0 is calm/full timer; 1 is danger/empty timer.
 */
export function samplePulseFxIntensity(player) {
  return 1 - samplePulseRatio(player);
}

/**
 * Maps danger intensity onto a post-FX range. The visual pulse is strongest
 * while the timer is full and drains toward the configured minimum as it drops.
 * @param {{ min: number, max: number }} range Configured FX range.
 * @param {number} intensity 0..1 danger intensity.
 * @returns {number}
 */
export function pulseFxDescendingValue(range, intensity) {
  const t = Math.min(1, Math.max(0, intensity));
  return range.max + (range.min - range.max) * t;
}
